/**
 * Pantalla OBLIGATORIA de cambio de contraseña.
 *
 * Se muestra (vía ProtectedRoute) cuando el usuario inició sesión con una
 * contraseña TEMPORAL autogenerada (`debe_cambiar_password=true`). Bloquea todo
 * el acceso hasta que el usuario fije su propia contraseña. El backend limpia el
 * flag al cambiarla; aquí lo limpiamos también en el store para liberar el gate.
 */
import { useState } from "react"
import type { FormEvent } from "react"
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from "@/components/ui"
import { Eye, EyeOff, ShieldAlert, LogOut } from "lucide-react"
import { toast } from "sonner"
import { post } from "@/services/api"
import { API_ENDPOINTS } from "@/config/api"
import { useAuthStore } from "@/stores"

export function ForcedPasswordChange() {
  const { user, markPasswordChanged, logout } = useAuthStore()
  const [actual, setActual] = useState("")
  const [nuevo, setNuevo] = useState("")
  const [confirmar, setConfirmar] = useState("")
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!actual || !nuevo) {
      toast.error("Completa todos los campos")
      return
    }
    if (nuevo.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres")
      return
    }
    if (nuevo === actual) {
      toast.error("La nueva contraseña debe ser distinta de la temporal")
      return
    }
    if (nuevo !== confirmar) {
      toast.error("Las contraseñas no coinciden")
      return
    }
    try {
      setLoading(true)
      const res = await post<{ success: boolean }>(
        API_ENDPOINTS.USERS.CHANGE_PASSWORD(user!.id_usuario),
        { password_actual: actual, password_nuevo: nuevo, confirmar_password: confirmar },
      )
      if (res.success) {
        toast.success("Contraseña actualizada. ¡Bienvenido!")
        markPasswordChanged()
      } else {
        toast.error("No se pudo cambiar la contraseña")
      }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      toast.error(typeof detail === "string" ? detail : "Error al cambiar la contraseña")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 text-amber-600">
            <ShieldAlert className="h-5 w-5" />
            <CardTitle>Cambia tu contraseña</CardTitle>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Tu contraseña es temporal. Por seguridad, debes establecer una nueva
            antes de continuar.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Contraseña temporal (la del correo)</Label>
              <Input
                type={show ? "text" : "password"}
                value={actual}
                onChange={(e) => setActual(e.target.value)}
                autoFocus
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label>Nueva contraseña</Label>
              <Input
                type={show ? "text" : "password"}
                value={nuevo}
                onChange={(e) => setNuevo(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label>Confirmar nueva contraseña</Label>
              <Input
                type={show ? "text" : "password"}
                value={confirmar}
                onChange={(e) => setConfirmar(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {show ? " Ocultar" : " Mostrar"} contraseñas
            </button>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Guardando…" : "Guardar y continuar"}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={logout}>
              <LogOut className="mr-1 h-4 w-4" /> Salir
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
